import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Instagram, Facebook } from 'lucide-react';
import { useSiteContent } from '../../context/SiteContentContext';

const Footer = () => {
  const { siteContent } = useSiteContent();
  const companyInfo = siteContent.companyInfo;
  const services = siteContent.services;
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <div className="h-12 w-44 sm:w-52 rounded-lg flex items-center justify-center overflow-hidden bg-white/0">
                <img
                  src={`${process.env.PUBLIC_URL}/logo_branca.png`}
                  alt={companyInfo?.name || "Logo"}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="hidden">
                <span className="font-bold text-xl">DRSUPORTI</span>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Soluções em TI</p>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              {companyInfo.slogan}
            </p>
            <p className="text-slate-400 text-sm">
              Atendemos empresas nos estados: {companyInfo.coverage.join(', ')}
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a
                href="#"
                className="w-10 h-10 rounded-lg bg-slate-800 hover:bg-orange-500 flex items-center justify-center transition-colors duration-300"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 rounded-lg bg-slate-800 hover:bg-orange-500 flex items-center justify-center transition-colors duration-300"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 rounded-lg bg-slate-800 hover:bg-orange-500 flex items-center justify-center transition-colors duration-300"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-white">Links Rápidos</h4>
            <ul className="space-y-3">
              {[
                { path: '/', label: 'Home' },
                { path: '/quem-somos', label: 'Quem Somos' },
                { path: '/servicos', label: 'Serviços' },
                { path: '/diferenciais', label: 'Diferenciais' },
                { path: '/blog', label: 'Blog' },
                { path: '/contato', label: 'Contato' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-orange-400 transition-colors duration-200 text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-white">Serviços</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.id}>
                  <Link
                    to="/servicos"
                    className="text-slate-400 hover:text-orange-400 transition-colors duration-200 text-sm"
                  >
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-white">Contato</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-slate-400">Telefone</p>
                  <a href={`tel:${companyInfo.phone}`} className="text-white hover:text-orange-400 transition-colors">
                    {companyInfo.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-slate-400">E-mail</p>
                  <a href={`mailto:${companyInfo.email}`} className="text-white hover:text-orange-400 transition-colors">
                    {companyInfo.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-slate-400">Atuação</p>
                  <p className="text-white">MA, PI e SP</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-400 text-sm">
              © {currentYear} {companyInfo.fullName}. Todos os direitos reservados.
            </p>
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <a href="#" className="hover:text-orange-400 transition-colors">Política de Privacidade</a>
              <a href="#" className="hover:text-orange-400 transition-colors">Termos de Uso</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
